/**
 * KlientMiniGraph - Kompakter Beziehungsgraph für einen einzelnen Klienten
 * Zeigt den Klienten als zentralen Knoten mit allen Verbindungen als Satelliten.
 * Leichtgewichtige Version des globalen NetzwerkGraph.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  RotateCcw,
  RefreshCw,
  Users,
  Building2,
  UserCircle,
  X,
  Expand,
  Download,
  Image,
  FileCode,
  FileText,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { exportGraphAsPNG, exportGraphAsSVG } from '@/lib/graphExport';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const TYP_CONFIG = {
  klient: { color: '#3B82F6', icon: Users, label: 'Betreuter' },
  juristische_person: { color: '#14B8A6', icon: Building2, label: 'Institution' },
  natuerliche_person: { color: '#A855F7', icon: UserCircle, label: 'Person' }
};

export default function KlientMiniGraph({ klientId, onOpenGlobal }) {
  const graphRef = useRef();
  const containerRef = useRef();
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 350 });
  const [pdfLoading, setPdfLoading] = useState(false);

  const exportAsPDF = async () => {
    setPdfLoading(true);
    try {
      // Canvas als base64 holen
      const canvas = containerRef.current?.querySelector('canvas');
      let graphImage = null;
      if (canvas) {
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = canvas.width;
        exportCanvas.height = canvas.height;
        const ctx = exportCanvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        ctx.drawImage(canvas, 0, 0);
        graphImage = exportCanvas.toDataURL('image/png');
      }

      const res = await fetch(`${API_URL}/api/netzwerk/klient/${klientId}/dossier-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ graph_image: graphImage })
      });

      if (!res.ok) throw new Error('PDF-Generierung fehlgeschlagen');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers.get('Content-Disposition');
      const match = disposition?.match(/filename="(.+)"/);
      link.download = match?.[1] || `Netzwerk_Dossier_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('PDF-Dossier exportiert');
    } catch (err) {
      console.error('PDF-Export Fehler:', err);
      toast.error('PDF-Export fehlgeschlagen');
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width || 600, height: 350 });
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/netzwerk/klient/${klientId}/graph-daten`);
      if (!res.ok) throw new Error('Graph-Daten nicht verfügbar');
      const data = await res.json();
      setGraphData({
        nodes: data.nodes || [],
        links: data.links || []
      });
    } catch (err) {
      console.error('Mini-Graph Fehler:', err);
    } finally {
      setLoading(false);
    }
  }, [klientId]);

  useEffect(() => {
    if (klientId) loadData();
  }, [klientId, loadData]);

  // Zoom to fit after data loads
  useEffect(() => {
    if (!loading && graphData.nodes.length > 0 && graphRef.current) {
      setTimeout(() => {
        graphRef.current?.zoomToFit(400, 40);
      }, 500);
    }
  }, [loading, graphData]);

  const stats = useMemo(() => ({
    institutionen: graphData.nodes.filter(n => n.typ === 'juristische_person').length,
    personen: graphData.nodes.filter(n => n.typ === 'natuerliche_person').length,
    links: graphData.links.length
  }), [graphData]);

  const paintNode = useCallback((node, ctx, globalScale) => {
    const nodeSize = node.val || 10;
    const isCenter = node.typ === 'klient';
    const isHovered = hoveredNode?.id === node.id;
    const isSelected = selectedNode?.id === node.id;

    // Glow-Effekt für Klient (Zentrum)
    if (isCenter) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeSize + 6, 0, 2 * Math.PI);
      ctx.fillStyle = `${node.color}18`;
      ctx.fill();
    }

    // Knoten
    ctx.beginPath();
    ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI);
    ctx.fillStyle = node.color || '#666';
    ctx.fill();

    // Highlight
    if (isHovered || isSelected) {
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2.5 / globalScale;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeSize + 2.5, 0, 2 * Math.PI);
      ctx.strokeStyle = node.color;
      ctx.lineWidth = 1.5 / globalScale;
      ctx.stroke();
    }

    // Label
    const fontSize = Math.max(isCenter ? 12 : 10, isCenter ? 12 : 10) / globalScale;
    ctx.font = `${isCenter ? 'bold ' : ''}${fontSize}px Inter, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    const label = node.name;
    const textWidth = ctx.measureText(label).width;
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fillRect(node.x - textWidth / 2 - 3, node.y + nodeSize + 3, textWidth + 6, fontSize + 4);

    ctx.fillStyle = isCenter ? '#1e293b' : '#475569';
    ctx.fillText(label, node.x, node.y + nodeSize + 5);
  }, [hoveredNode, selectedNode]);

  const paintLink = useCallback((link, ctx, globalScale) => {
    const start = link.source;
    const end = link.target;
    if (typeof start !== 'object' || typeof end !== 'object') return;

    const isHovered = hoveredNode &&
      (link.source.id === hoveredNode.id || link.target.id === hoveredNode.id);

    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.strokeStyle = isHovered ? link.color : `${link.color}80`;
    ctx.lineWidth = isHovered ? 2.5 / globalScale : 1.5 / globalScale;
    ctx.stroke();

    // Beziehungs-Label bei Hover
    if (isHovered && link.label) {
      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;
      const labelSize = 9 / globalScale;
      ctx.font = `${labelSize}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      const tw = ctx.measureText(link.label).width;
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.fillRect(midX - tw / 2 - 3, midY - labelSize / 2 - 2, tw + 6, labelSize + 4);
      ctx.fillStyle = link.color;
      ctx.fillText(link.label, midX, midY - labelSize / 2 + 1);
    }
  }, [hoveredNode]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[350px] bg-slate-50 rounded-xl border border-slate-200">
        <RefreshCw className="w-5 h-5 animate-spin text-slate-400 mr-2" />
        <span className="text-sm text-slate-500">Graph wird geladen...</span>
      </div>
    );
  }

  if (graphData.nodes.length <= 1) {
    return (
      <div className="flex flex-col items-center justify-center h-[200px] bg-slate-50 rounded-xl border border-slate-200 border-dashed">
        <Users className="w-8 h-8 text-slate-300 mb-2" />
        <p className="text-sm text-slate-400">Keine Verbindungen zum Visualisieren</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="klient-mini-graph">
      {/* Compact Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500" /> 1 Betreuter</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-teal-500" /> {stats.institutionen} Inst.</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-purple-500" /> {stats.personen} Pers.</span>
          <span className="text-slate-300">|</span>
          <span>{stats.links} Verbindungen</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => graphRef.current?.zoom(graphRef.current.zoom() * 1.3, 300)} title="Vergrößern">
            <ZoomIn className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => graphRef.current?.zoom(graphRef.current.zoom() / 1.3, 300)} title="Verkleinern">
            <ZoomOut className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => graphRef.current?.zoomToFit(300, 40)} title="Einpassen">
            <Maximize2 className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={loadData} title="Aktualisieren">
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2" title="Exportieren" data-testid="mini-graph-export-btn">
                <Download className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  const ok = exportGraphAsPNG(containerRef.current, `beziehungsnetz`);
                  if (ok) toast.success('PNG exportiert');
                  else toast.error('Export fehlgeschlagen');
                }}
                data-testid="export-png-btn"
              >
                <Image className="w-4 h-4 mr-2" /> Als PNG exportieren
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  const ok = exportGraphAsSVG(graphData, {
                    width: 800,
                    height: 500,
                    title: `Beziehungsnetz`,
                    filename: `beziehungsnetz`
                  });
                  if (ok) toast.success('SVG exportiert');
                  else toast.error('Export fehlgeschlagen');
                }}
                data-testid="export-svg-btn"
              >
                <FileCode className="w-4 h-4 mr-2" /> Als SVG exportieren
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={exportAsPDF}
                disabled={pdfLoading}
                data-testid="export-pdf-btn"
              >
                {pdfLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                {pdfLoading ? 'Wird generiert...' : 'Als PDF-Dossier exportieren'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {onOpenGlobal && (
            <Button variant="outline" size="sm" className="h-7 px-2 ml-1" onClick={onOpenGlobal} title="Im globalen Graph öffnen" data-testid="open-global-graph-btn">
              <Expand className="w-3.5 h-3.5 mr-1" /> Erweitern
            </Button>
          )}
        </div>
      </div>

      {/* Graph */}
      <div
        ref={containerRef}
        className="relative bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 overflow-hidden"
        style={{ height: 350 }}
        data-testid="mini-graph-container"
      >
        <ForceGraph2D
          ref={graphRef}
          width={dimensions.width}
          height={350}
          graphData={graphData}
          nodeCanvasObject={paintNode}
          nodePointerAreaPaint={(node, color, ctx) => {
            ctx.beginPath();
            ctx.arc(node.x, node.y, (node.val || 10) + 5, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
          }}
          linkCanvasObject={paintLink}
          onNodeClick={(node) => setSelectedNode(node === selectedNode ? null : node)}
          onNodeHover={(node) => {
            setHoveredNode(node);
            if (containerRef.current) containerRef.current.style.cursor = node ? 'pointer' : 'grab';
          }}
          cooldownTicks={80}
          d3AlphaDecay={0.04}
          d3VelocityDecay={0.25}
          backgroundColor="transparent"
          enableZoomInteraction={true}
          enablePanInteraction={true}
        />

        {/* Selected Node Info */}
        {selectedNode && selectedNode.typ !== 'klient' && (
          <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg border shadow-lg p-3 z-10" data-testid="mini-graph-node-detail">
            <button onClick={() => setSelectedNode(null)} className="absolute top-2 right-2 text-slate-400 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${selectedNode.color}15` }}>
                {selectedNode.typ === 'juristische_person' ? (
                  <Building2 className="w-4 h-4" style={{ color: selectedNode.color }} />
                ) : (
                  <UserCircle className="w-4 h-4" style={{ color: selectedNode.color }} />
                )}
              </div>
              <div>
                <p className="font-medium text-sm text-slate-800">{selectedNode.name}</p>
                <Badge variant="secondary" className="text-xs mt-0.5">
                  {TYP_CONFIG[selectedNode.typ]?.label}
                </Badge>
              </div>
            </div>
          </div>
        )}

        {/* Legende (compact) */}
        <div className="absolute top-2 left-2 bg-white/80 backdrop-blur-sm rounded-md px-2 py-1.5 text-[10px] space-y-1">
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500" /><span className="text-slate-600">Betreuter</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-teal-500" /><span className="text-slate-600">Institution</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-purple-500" /><span className="text-slate-600">Person</span></div>
        </div>
      </div>
    </div>
  );
}
